import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  Palette, 
  Ruler, 
  User, 
  Check, 
  X, 
  Info,
  MessageCircle,
  Phone,
  Mail
} from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import Button from '../../components/UI/Button';
import SEOHead from '../../components/SEO/SEOHead';
import { useAnimals } from '../../contexts/AnimalsContext';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import { PublicAnimal } from '../../services/api';
import AdoptionApplicationModal from '../../components/Modals/AdoptionApplicationModal';

const DogDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getAnimal } = useAnimals();
  const { settings } = useSystemSettings();
  const [dog, setDog] = useState<PublicAnimal | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdoptionModalOpen, setIsAdoptionModalOpen] = useState(false);

  useEffect(() => {
    const fetchDog = async () => {
      if (!id) {
        setError('ID invalid');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const dogData = await getAnimal(id);
        
        if (dogData) {
          setDog(dogData);
        } else {
          setError('Câinele nu a fost găsit');
        }
      } catch (err) {
        console.error('Error fetching dog:', err);
        setError('Eroare la încărcarea datelor câinelui');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDog();
  }, [id, getAnimal]);

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="Se încarcă..."
          subtitle="Așteptăm să încărcăm informațiile despre acest prieten blănos"
          image="https://images.pexels.com/photos/58997/pexels-photo-58997.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        />
        <Container>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Se încarcă detaliile câinelui...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error || !dog) {
    return (
      <>
        <PageHeader
          title="Câine nu a fost găsit"
          subtitle="Din păcate, acest câine nu există sau nu mai este disponibil"
          image="https://images.pexels.com/photos/58997/pexels-photo-58997.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        />
        <Container>
          <div className="text-center py-12">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-md mx-auto">
              <p>{error}</p>
            </div>
            <Link
              to="/caini"
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors inline-block"
            >
              Vezi toți câinii
            </Link>
          </div>
        </Container>
      </>
    );
  }

  const minimumAge = settings?.minimumAge || '18';

  return (
    <>
      <SEOHead 
        title={`Adoptă pe ${dog.name} - Câine ${dog.gender === 'male' ? 'Mascul' : 'Femelă'} pentru Adopție în București`}
        description={`Adoptă pe ${dog.name}, ${dog.gender === 'male' ? 'un câine mascul' : 'o câine femelă'} de ${dog.age}, talie ${dog.size}. ${dog.description.substring(0, 120)}... Adopție gratuită din Adăpostul Odăi, Sector 1 București.`}
        keywords={`adopta ${dog.name.toLowerCase()}, adopta caine ${dog.gender === 'male' ? 'mascul' : 'femela'}, adopta caine ${dog.age}, adopta caine ${dog.size}, caini pentru adopție bucurești, adăpost odăi`}
        url={`/caini/${dog.id}`}
        type="article"
        image={dog.image}
        animal={{
          name: dog.name,
          age: dog.age,
          gender: dog.gender,
          description: dog.description
        }}
      />
      <PageHeader
        title={dog.name}
        subtitle={`${dog.gender === 'male' ? 'Mascul' : 'Femelă'} • ${dog.age} • ${dog.size === 'mic' ? 'Mic' : dog.size === 'mediu' ? 'Mediu' : 'Mare'}`}
        image={dog.image}
      />

      <Container>
        <div className="mb-6">
          <Link
            to="/caini"
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Înapoi la toți câinii
          </Link>
        </div>

        {/* Top Section: Image and Adoption Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Image - Left */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="relative">
                <img
                  src={dog.image}
                  alt={dog.name}
                  className="w-full h-[500px] lg:h-[600px] object-contain bg-gray-50"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/default-dog.svg';
                  }}
                />
              </div>
            </motion.div>
          </div>

          {/* Adoption Sidebar - Right */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg shadow-md p-6 h-full"
            >
              <h3 className="text-xl font-bold text-primary mb-4">Adoptă pe {dog.name}</h3>
              
              <div className="bg-accent-100 p-4 rounded-lg mb-6">
                <div className="flex items-start mb-3">
                  <Info size={20} className="text-accent mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-accent mb-1">Cerințe pentru adopție</h4>
                    <ul className="text-sm text-accent-dark space-y-1">
                      <li>• Vârsta minimă: {minimumAge} ani</li>
                      <li>• Act de identitate</li>
                      <li>• Formularul de adopție completat</li>
                      <li>• Acord familial pentru adopție</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <Button 
                  onClick={() => setIsAdoptionModalOpen(true)}
                  variant="accent" 
                  fullWidth
                  className="flex items-center justify-center"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Aplică pentru adopție
                </Button>
                
                <a
                  href={`tel:${settings?.phone || '+40750209751'}`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors"
                >
                  <Phone size={18} className="mr-2" />
                  Sună acum
                </a>
                
                <Link
                  to={`/contact?subject=Adopție ${dog.name}&message=Sunt interesat să adopt pe ${dog.name}. Vă rog să mă contactați pentru mai multe informații.`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Mail size={18} className="mr-2" />
                  Trimite email
                </Link>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-800 mb-2">Program de vizitare</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {settings?.operatingHours || 'Luni - Vineri: 09:00 - 17:00'}
                </p>
                <p className="text-xs text-gray-500">
                  Te rugăm să suni înainte de a veni pentru a programa o întâlnire cu {dog.name}.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section: Dog Details - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-lg shadow-md overflow-hidden"
        >
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-3xl font-bold text-primary mb-2">{dog.name}</h1>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <User size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-gray-600">Gen</div>
                <div className="font-semibold">{dog.gender === 'male' ? 'Mascul' : 'Femelă'}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <Calendar size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-gray-600">Vârstă</div>
                <div className="font-semibold">{dog.age}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <Ruler size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-gray-600">Talie</div>
                <div className="font-semibold">
                  {dog.size === 'mic' ? 'Mic' : dog.size === 'mediu' ? 'Mediu' : 'Mare'}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <Palette size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-gray-600">Culoare</div>
                <div className="font-semibold">{dog.color}</div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-3">Despre {dog.name}</h3>
              <p className="text-gray-700 leading-relaxed">{dog.description}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-3">Starea sănătății</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center">
                  {dog.vaccinated ? (
                    <Check size={20} className="text-green-500 mr-2" />
                  ) : (
                    <X size={20} className="text-red-500 mr-2" />
                  )}
                  <span className={dog.vaccinated ? 'text-green-700' : 'text-red-700'}>
                    Vaccinat
                  </span>
                </div>
                <div className="flex items-center">
                  {dog.neutered ? (
                    <Check size={20} className="text-green-500 mr-2" />
                  ) : (
                    <X size={20} className="text-red-500 mr-2" />
                  )}
                  <span className={dog.neutered ? 'text-green-700' : 'text-red-700'}>
                    Sterilizat
                  </span>
                </div>
                <div className="flex items-center">
                  {dog.chipped ? (
                    <Check size={20} className="text-green-500 mr-2" />
                  ) : (
                    <X size={20} className="text-red-500 mr-2" />
                  )}
                  <span className={dog.chipped ? 'text-green-700' : 'text-red-700'}>
                    Microcipat
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Adoption Application Modal */}
      {dog && (
        <AdoptionApplicationModal
          isOpen={isAdoptionModalOpen}
          onClose={() => setIsAdoptionModalOpen(false)}
          animalId={dog.id}
          animalName={dog.name}
        />
      )}
    </>
  );
};

export default DogDetails;